<script lang="ts">
    import type * as t from "../types";
    import type {
        CountryCode,
        DetailedValue,
        E164Number,
    } from "svelte-tel-input/types";

    import { TelInput, normalizedCountries } from "svelte-tel-input";
    import AttentiveDisclaimer from "./AttentiveDisclaimer.svelte";
    import { getAppContext } from "~/quiz-store";
    import { cn } from "~/utils";
    import ActionButton from "./ActionButton.svelte";

    export let onSubmit: (e: SubmitEvent) => void;
    export let enabledInputTypes: t.SegmentResultStep["enabledInputTypes"];
    export let formInputType: "phone" | "email";
    export let errors: Record<string, string> = {};
    export let networkStatus: "idle" | "loading" = "idle";
    export let emailInputPlaceholder: string;

    let selectedCountry: CountryCode | null = "US";
    let phoneInputValue: E164Number | null = null;
    let detailedPhoneInputValue: DetailedValue | null = null;
    let isPhoneInputValid = true;

    const STORE_HANDLE = window.Shopify.shop;
    const ctx = getAppContext();
    const { state, telemetry } = ctx;
    const emailMktConfig = $state.emailMarketingConfig;
    const shop = $state.shop;

    async function handleSubmitForm(e: SubmitEvent) {
        e.preventDefault();

        if (formInputType === "phone" && !isPhoneInputValid) {
            errors = { ...errors, phone: "invalid phone number" };
            return;
        }

        errors = {};
        onSubmit(e);
    }
</script>

<form
    on:submit={handleSubmitForm}
    class="flex w-full flex-col items-center gap-2"
>
    <input name="store" type="hidden" value={STORE_HANDLE} />
    <input name="session_id" type="hidden" value={telemetry.sessionId} />

    {#if enabledInputTypes.size > 1}
        <fieldset class="flex w-3/4 rounded-lg bg-gray-200 p-1">
            {#if enabledInputTypes.has("email")}
                <label
                    for="email-form-input"
                    class={cn(
                        "flex h-11 flex-1 flex-col justify-center rounded p-1 text-center font-semibold text-gray-800 transition-[background]",
                        formInputType === "email" && "bg-white",
                    )}
                >
                    Email
                </label>
                <input
                    id="email-form-input"
                    type="radio"
                    name="formInputType"
                    value={"email"}
                    checked={formInputType === "email"}
                    class="invisible"
                    bind:group={formInputType}
                />
            {/if}

            {#if enabledInputTypes.has("phone")}
                <label
                    for="phone-form-input"
                    class={cn(
                        "flex h-11 flex-1 flex-col justify-center rounded p-1 text-center font-semibold text-gray-800 transition-[background]",
                        formInputType === "phone" && "bg-white",
                    )}>Mobile</label
                >
                <input
                    id="phone-form-input"
                    type="radio"
                    name="formInputType"
                    value={"phone"}
                    checked={formInputType === "phone"}
                    class="invisible"
                    bind:group={formInputType}
                />
            {/if}
        </fieldset>
    {/if}

    <div class="mt-4 flex w-full flex-1 flex-col items-stretch gap-2">
        {#if formInputType === "email"}
            <input
                class="h-12 rounded-lg border-2 border-gray-200 px-3"
                name="email"
                type="email"
                placeholder={emailInputPlaceholder}
                required
            />
        {:else}
            {#if errors.phone != null}
                <span class="text-sm text-rose-500">{errors.phone}</span>
            {/if}
            <div
                class={cn(
                    "phone-input-wrapper flex h-12 items-stretch rounded-lg border-2 border-gray-200 px-3",
                    errors.phone != null && "invalid",
                )}
            >
                <select
                    class={cn(
                        "country-select w-[30%]",
                        !isPhoneInputValid && "invalid",
                    )}
                    aria-label="Select country"
                    name="Country"
                    bind:value={selectedCountry}
                >
                    <option value={null} hidden={selectedCountry !== null}
                        >Please select</option
                    >
                    {#each normalizedCountries as currentCountry (currentCountry.id)}
                        <option
                            value={currentCountry.iso2}
                            selected={currentCountry.iso2 === selectedCountry}
                            aria-selected={currentCountry.iso2 ===
                                selectedCountry}
                        >
                            {currentCountry.iso2} (+{currentCountry.dialCode})
                        </option>
                    {/each}
                </select>
                <TelInput
                    bind:country={selectedCountry}
                    bind:value={phoneInputValue}
                    bind:detailedValue={detailedPhoneInputValue}
                    bind:valid={isPhoneInputValid}
                    class="basic-tel-input flex-1 px-3"
                    name="phone"
                    required
                />
                <input
                    type="hidden"
                    name="phone"
                    value={detailedPhoneInputValue?.phoneNumber ?? undefined}
                />
            </div>
        {/if}

        {#if emailMktConfig.enabledProviders.includes("attentive")}
            <AttentiveDisclaimer {formInputType} shopName={shop.name} />
        {/if}

        <ActionButton type="submit" {networkStatus} class="mt-4">
            <span slot="title">
                <slot name="ctaText">Claim your prize</slot>
            </span>
        </ActionButton>
    </div>
</form>

<style>
    input[type="radio"] {
        width: 0;
        height: 0;
        border: 0;
    }

    fieldset {
        box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    }
</style>
